import { StyleSheet } from '@react-pdf/renderer';
import { PDF_COLORS, PDF_FONT_SIZES } from './pdfStyles';
import { PDF_FONTS } from './fonts';

// Import font registration - this ensures fonts are registered before use
import './fonts';

/**
 * Typography Styles for PDF Generation
 * 
 * Maps Material-UI typography variants to PDF-compatible styles.
 * Provides consistent typography across all PDF documents.
 * Uses Montserrat font family to match the main application.
 */

// Typography mapping from Material-UI variants to PDF styles
export const typographyStyles = StyleSheet.create({
  // Material-UI variant mappings
  h1: {
    fontSize: 24,
    fontWeight: 'bold',
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT,
    marginBottom: 16,
    lineHeight: 1.2,
  },
  
  h2: {
    fontSize: 20,
    fontWeight: 'bold',
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT,
    marginBottom: 14,
    lineHeight: 1.2,
  },
  
  h3: {
    fontSize: PDF_FONT_SIZES.TITLE, // 18
    fontWeight: 'bold',
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT,
    marginBottom: 5,
    lineHeight: 1.3,
  },
  
  h4: {
    fontSize: PDF_FONT_SIZES.SUBTITLE, // 16
    fontWeight: 'bold',
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT,
    marginBottom: 5,
    lineHeight: 1.3,
  },
  
  h5: {
    fontSize: PDF_FONT_SIZES.HEADING, // 14
    fontWeight: 'bold',
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT,
    marginBottom: 8,
    lineHeight: 1.4,
  },
  
  h6: {
    fontSize: PDF_FONT_SIZES.SUBHEADING, // 12
    fontWeight: 'bold',
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT,
    marginBottom: 6,
    lineHeight: 1.4,
  },
  
  // Body text variants
  body1: {
    fontSize: 11,
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT,
    lineHeight: 1.5,
    marginBottom: 4,
  },
  
  body2: {
    fontSize: PDF_FONT_SIZES.BODY, // 10
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT,
    lineHeight: 1.4,
    marginBottom: 4,
  },
  
  // Subtitle variants
  subtitle1: {
    fontSize: PDF_FONT_SIZES.SUBHEADING, // 12
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT,
    lineHeight: 1.4,
    marginBottom: 6,
  },
  
  subtitle2: {
    fontSize: 11,
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT_LIGHT,
    lineHeight: 1.4,
    marginBottom: 4,
  },
  
  // Caption and overline
  caption: {
    fontSize: PDF_FONT_SIZES.CAPTION, // 9
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT_LIGHT,
    lineHeight: 1.3,
    marginBottom: 2,
  },
  
  overline: {
    fontSize: PDF_FONT_SIZES.SMALL, // 8
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT_MUTED,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 2,
  },
  
  // Button text (for labels)
  button: {
    fontSize: PDF_FONT_SIZES.BODY,
    fontWeight: 'bold',
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT,
    textTransform: 'uppercase',
  },
});

// Specialized typography for specific PDF contexts
export const specializedTypography = StyleSheet.create({
  // Patient information
  patientName: {
    fontSize: PDF_FONT_SIZES.BODY,
    fontWeight: 'bold',
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT,
  },
  
  patientId: {
    fontSize: PDF_FONT_SIZES.BODY,
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT_LIGHT,
    fontStyle: 'italic',
  },
  
  // Medical document specific
  diagnosis: {
    fontSize: PDF_FONT_SIZES.BODY,
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT,
    lineHeight: 1.4,
  },
  
  medication: {
    fontSize: PDF_FONT_SIZES.BODY,
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT,
    fontWeight: 'bold',
  },
  
  dosage: {
    fontSize: PDF_FONT_SIZES.CAPTION,
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT_LIGHT,
    fontStyle: 'italic',
  },
  
  // Financial/billing
  currency: {
    fontSize: PDF_FONT_SIZES.BODY,
    fontFamily: PDF_FONTS.PRIMARY,
    fontWeight: 'bold',
    color: PDF_COLORS.TEXT,
    textAlign: 'right',
  },
  
  total: {
    fontSize: PDF_FONT_SIZES.SUBHEADING,
    fontFamily: PDF_FONTS.PRIMARY,
    fontWeight: 'bold',
    color: PDF_COLORS.TEXT,
    textAlign: 'right',
  },
  
  // Date and time
  date: {
    fontSize: PDF_FONT_SIZES.CAPTION,
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT_LIGHT,
  },
  
  timestamp: {
    fontSize: PDF_FONT_SIZES.SMALL,
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT_MUTED,
    fontStyle: 'italic',
  },
  
  // Labels and form fields
  fieldLabel: {
    fontSize: PDF_FONT_SIZES.CAPTION,
    fontFamily: PDF_FONTS.PRIMARY,
    fontWeight: 'bold',
    color: PDF_COLORS.TEXT,
    marginBottom: 2,
  },
  
  fieldValue: {
    fontSize: PDF_FONT_SIZES.BODY,
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT,
    marginBottom: 2,
  },
  
  // Status and indicators
  statusActive: {
    fontSize: PDF_FONT_SIZES.CAPTION,
    fontFamily: PDF_FONTS.PRIMARY,
    fontWeight: 'bold',
    color: PDF_COLORS.SUCCESS,
  },
  
  statusInactive: {
    fontSize: PDF_FONT_SIZES.CAPTION,
    fontFamily: PDF_FONTS.PRIMARY,
    fontWeight: 'bold',
    color: PDF_COLORS.ERROR,
  },
  
  statusPending: {
    fontSize: PDF_FONT_SIZES.CAPTION,
    fontFamily: PDF_FONTS.PRIMARY,
    fontWeight: 'bold',
    color: PDF_COLORS.WARNING,
  },
  
  // Hospital/clinic branding
  hospitalName: {
    fontSize: PDF_FONT_SIZES.SUBTITLE,
    fontFamily: PDF_FONTS.PRIMARY,
    fontWeight: 'bold',
    color: PDF_COLORS.PRIMARY,
  },
  
  hospitalAddress: {
    fontSize: PDF_FONT_SIZES.CAPTION,
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT_LIGHT,
    lineHeight: 1.3,
  },
  
  // Signatures
  signatureName: {
    fontSize: PDF_FONT_SIZES.BODY,
    fontFamily: PDF_FONTS.PRIMARY,
    fontWeight: 'bold',
    color: PDF_COLORS.TEXT,
    textAlign: 'center',
  },
  
  signatureTitle: {
    fontSize: PDF_FONT_SIZES.CAPTION,
    fontFamily: PDF_FONTS.PRIMARY,
    color: PDF_COLORS.TEXT_LIGHT,
    textAlign: 'center',
    fontStyle: 'italic',
  },
});

/**
 * Utility function to get typography style by variant name
 * @param variant - Typography variant name
 * @returns Style object or undefined if variant doesn't exist
 */
export const getTypographyStyle = (variant: keyof typeof typographyStyles) => {
  return typographyStyles[variant];
};

/**
 * Utility function to merge typography styles with custom styles
 * @param variant - Base typography variant
 * @param customStyles - Custom style overrides
 * @returns Merged style object
 */
export const mergeTypographyStyle = (
  variant: keyof typeof typographyStyles,
  customStyles: Record<string, any> = {}
) => {
  return {
    ...typographyStyles[variant],
    ...customStyles,
  };
};

/**
 * Typography style mappings for HTML elements
 * Used by the HTML to PDF converter
 */
export const htmlElementStyles = StyleSheet.create({
  h1: typographyStyles.h1,
  h2: typographyStyles.h2,
  h3: typographyStyles.h3,
  h4: typographyStyles.h4,
  h5: typographyStyles.h5,
  h6: typographyStyles.h6,
  p: typographyStyles.body2,
  span: typographyStyles.body2,
  strong: {
    ...typographyStyles.body2,
    fontWeight: 'bold',
  },
  em: {
    ...typographyStyles.body2,
    fontStyle: 'italic',
  },
  small: {
    ...typographyStyles.caption,
  },
});

